/**
 * Client-generated UUIDv7.
 *
 * Every offline write needs an id before it can be sent — that is the whole
 * point of the queue — so the id has to come from the device.
 *
 * `crypto.randomUUID` is not enough on its own: it only exists in a *secure
 * context*, so it is missing on `http://192.168.x.x:5173`, which is exactly
 * how this app gets opened on a phone on the church wifi. Losing every
 * attendance save on the LAN to a missing built-in is not a trade worth
 * making, so this falls back to `getRandomValues`, which is always there.
 *
 * v7 rather than v4 because the leading 48 bits are a timestamp: ids sort in
 * the order they were created, which keeps the primary-key index tidy and
 * makes a queue dump readable.
 */
export function randomUUIDv7(): string {
  const bytes = randomBytes(16)
  const timestamp = Date.now()

  // 48-bit big-endian milliseconds.
  bytes[0] = (timestamp / 2 ** 40) & 0xff
  bytes[1] = (timestamp / 2 ** 32) & 0xff
  bytes[2] = (timestamp / 2 ** 24) & 0xff
  bytes[3] = (timestamp / 2 ** 16) & 0xff
  bytes[4] = (timestamp / 2 ** 8) & 0xff
  bytes[5] = timestamp & 0xff

  // Version 7, RFC 4122 variant.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length)
  const webCrypto = globalThis.crypto
  if (webCrypto?.getRandomValues) {
    webCrypto.getRandomValues(bytes)
    return bytes
  }
  // No crypto at all (an old React Native runtime). These ids are dedupe keys,
  // not secrets, so a weaker source is survivable — silently failing to
  // generate one is not.
  for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  return bytes
}
